import { buildVargaHouses, planetPositions, PLANET_COLORS, LAGNA_POLYGON } from '@astro/vedastro'

/**
 * The numbered divisional chart — VedAstro's own North Indian grid artwork
 * with our rashi-number + graha layer drawn on top. Shared by the Charts tab
 * (on screen and its SVG download) and the PDF report, so every copy of a
 * chart is drawn by the same code and can never drift apart.
 */

export const NUMBER_FONT_SIZE = 26
export const NUMBER_FILL = '#c2751a' // the grid's own ochre, so numbers read as part of the chart
const PLANET_FILL = '#1c1917'
// Same stack VedAstro's own chart text uses — keeps a standalone SVG/PNG
// (no page CSS to inherit) from falling back to a serif font.
const OVERLAY_FONT = 'Inter, &quot;Segoe UI&quot;, Arial, sans-serif'
const PLANET_LAYER_RE = /<g id="PlanetLayer">[\s\S]*?<\/g>/

/**
 * The drawable text for one house — its rashi number at the cell's inner
 * vertex, and its grahas stacked centred in the cell body.
 * Every graha carries its superscript degree ("Su⁰⁴" = Sun 4° into its sign)
 * at the abbreviation's top-right; in a divisional chart that is the degree
 * within the varga sign, straight from the engine's own DegreesIn field.
 */
export function houseGlyphs(h) {
  const [nx, ny, anchor] = h.numberPos
  const spots = planetPositions(h.center, h.planets.length)
  return {
    number: { x: nx, y: ny, anchor, text: h.rashi },
    planets: h.planets.map((p, i) => {
      const s = spots[i]
      return {
        key: p.name,
        text: p.abbr,
        fill: PLANET_COLORS[p.name] || PLANET_FILL,
        ...s,
        deg:
          p.deg != null
            ? {
                text: String(p.deg).padStart(2, '0'),
                x: s.x + s.fontSize * 0.6,
                y: s.y - s.fontSize * 0.45,
                fontSize: Math.round(s.fontSize * 0.55),
              }
            : null,
      }
    }),
  }
}

/**
 * Overlay inputs from the two shared tables. House 1's own per-varga cusp
 * sign IS that varga's Ascendant, so the house ring comes straight from the
 * engine. `middle` is kept only as a fallback: it's the Bhava Madhya (the
 * true Ascendant degree), not `begin`, which is the Bhava-Sandhi junction
 * ~15° earlier and would round into the previous sign and rotate the whole
 * chart by a house.
 */
export function chartLayoutData(planets, houses) {
  const house1 = houses.find((h) => h.house === 1)
  return { planets, vargas: house1?.vargas, ascendantLongitude: house1?.middle }
}

/** House ring + graha placement for one varga, or null when unverified. */
export function buildChartLayout(layoutData, shortKey) {
  if (!layoutData || !shortKey) return null
  return buildVargaHouses({
    vargaLagna: layoutData.vargas?.[shortKey]?.sign,
    ascendantLongitude: layoutData.ascendantLongitude,
    planets: layoutData.planets,
    shortKey,
  })
}

/**
 * VedAstro's grid artwork is identical across chart types — only its
 * PlanetLayer (full names, no rashi numbers) differs, so we cut that out
 * and draw our own numbered layer on top instead.
 */
export function stripPlanetLayer(svg) {
  return svg ? svg.replace(PLANET_LAYER_RE, '') : null
}

/** One self-contained SVG string: the grid with our numbered layer baked in. */
export function buildNumberedSvg(gridOnlySvg, layout) {
  if (!layout || !gridOnlySvg) return null
  const wash = `<polygon points="${LAGNA_POLYGON}" fill="${NUMBER_FILL}" opacity="0.08"/>`
  const overlay = layout.houses
    .map((h) => {
      const { number, planets } = houseGlyphs(h)
      const rashi =
        `<text x="${number.x}" y="${number.y}" text-anchor="${number.anchor}" font-size="${NUMBER_FONT_SIZE}"` +
        ` font-weight="700" fill="${NUMBER_FILL}">${number.text}</text>`
      return (
        rashi +
        planets
          .map(
            (p) =>
              `<text x="${p.x}" y="${p.y}" text-anchor="middle" font-size="${p.fontSize}"` +
              ` font-weight="600" fill="${p.fill}">${p.text}</text>` +
              (p.deg
                ? `<text x="${p.deg.x}" y="${p.deg.y}" text-anchor="start" font-size="${p.deg.fontSize}"` +
                  ` font-weight="600" fill="${p.fill}">${p.deg.text}</text>`
                : ''),
          )
          .join('')
      )
    })
    .join('')
  return gridOnlySvg.replace(
    '</svg>',
    `<g id="RashiLayer" font-family="${OVERLAY_FONT}">${wash}${overlay}</g></svg>`,
  )
}
